import { buildIndexedNameArray } from './draco-metadata'
import { resolveDracoAsset } from './draco-asset-resolver'
import { splitSubObjects, type FlatMeshData } from './meme-decoder'

export interface DecodedRecoveredSubObjectBounds {
  name: string
  materialName: string | null
  center: { x: number; y: number; z: number }
  size: { x: number; y: number; z: number }
}

interface DracoModule {
  Decoder: new () => DracoDecoder
  DecoderBuffer: new () => DracoDecoderBuffer
  MetadataQuerier: new () => DracoMetadataQuerier
  Mesh: new () => DracoMesh
  DracoFloat32Array: new () => DracoTypedArray
  DracoUInt32Array: new () => DracoTypedArray
  POSITION: number
  NORMAL: number
  TEX_COORD: number
  destroy(obj: unknown): void
}

interface DracoDecoder {
  DecodeBufferToMesh(buffer: DracoDecoderBuffer, mesh: DracoMesh): { ok(): boolean }
  GetAttributeId(mesh: DracoMesh, type: number): number
  GetAttribute(mesh: DracoMesh, attrId: number): DracoAttribute
  GetAttributeFloatForAllPoints(mesh: DracoMesh, attr: DracoAttribute, arr: DracoTypedArray): void
  GetFaceFromMesh(mesh: DracoMesh, faceIdx: number, faceArray: DracoTypedArray): void
  GetMetadata(mesh: DracoMesh): DracoMetadata | null
  GetAttributeMetadata(mesh: DracoMesh, attrId: number): DracoMetadata | null
}

interface DracoDecoderBuffer {
  Init(data: Int8Array, length: number): void
}

interface DracoMesh {
  ptr: number
  num_points(): number
  num_faces(): number
  num_attributes(): number
}

interface DracoAttribute {
  num_components(): number
}

interface DracoMetadata {
  ptr: number
}

interface DracoMetadataQuerier {
  HasEntry(metadata: DracoMetadata, key: string): boolean
  GetStringEntry(metadata: DracoMetadata, key: string): string
  GetIntEntry(metadata: DracoMetadata, key: string): number
  NumEntries(metadata: DracoMetadata): number
  GetEntryName(metadata: DracoMetadata, index: number): string
}

interface DracoTypedArray {
  GetValue(index: number): number
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export interface DracoDecodeOptions {
  fetchFn: FetchLike
  origin: string
  onProgress?: (stage: string, detail?: string) => void
}

let decoderModule: DracoModule | null = null
let decoderPromise: Promise<DracoModule> | null = null

async function getDecoder(options: DracoDecodeOptions): Promise<DracoModule> {
  if (decoderModule) {
    return decoderModule
  }

  if (decoderPromise) {
    return decoderPromise
  }

  decoderPromise = (async function () {
    options.onProgress && options.onProgress('resolve-asset-start')
    const resolvedAsset = await resolveDracoAsset(options.fetchFn, options.origin)
    options.onProgress && options.onProgress('resolve-asset-ok', resolvedAsset.scriptUrl + ' bytes=' + resolvedAsset.scriptText.length)

    options.onProgress && options.onProgress('eval-script-start')
    // eslint-disable-next-line no-eval
    ;(0, eval)(resolvedAsset.scriptText)
    options.onProgress && options.onProgress('eval-script-ok')

    const dracoDecoderModule = (globalThis as unknown as Record<string, unknown>).DracoDecoderModule as
      ((config?: Record<string, unknown>) => Promise<DracoModule>) | undefined

    if (typeof dracoDecoderModule !== 'function') {
      throw new Error('Draco decoder bootstrap failed for ' + resolvedAsset.scriptUrl)
    }

    const assetBaseUrl = resolvedAsset.assetBaseUrl
    options.onProgress && options.onProgress('wasm-init-start', 'base=' + assetBaseUrl)
    const module = await dracoDecoderModule({
      locateFile: function (filename: string) {
        return assetBaseUrl + '/' + filename
      },
    })
    options.onProgress && options.onProgress('wasm-init-ok')

    decoderModule = module
    return module
  })().catch(function (error) {
    decoderPromise = null
    throw error
  })

  return decoderPromise
}

function decodeMesh(draco: DracoModule, buffer: ArrayBuffer): FlatMeshData {
  const decoder = new draco.Decoder()
  const decoderBuffer = new draco.DecoderBuffer()
  const mesh = new draco.Mesh()

  try {
    const data = new Int8Array(buffer)
    decoderBuffer.Init(data, data.length)

    const status = decoder.DecodeBufferToMesh(decoderBuffer, mesh)
    if (!status.ok()) {
      throw new Error('Draco decoding failed')
    }

    const numVertices = mesh.num_points()
    const numFaces = mesh.num_faces()
    const numAttributes = mesh.num_attributes()

    const positions = extractFloat32Attribute(draco, decoder, mesh, draco.POSITION, 3, numVertices)
    if (!positions) {
      throw new Error('Mesh has no position attribute')
    }

    const normals = extractFloat32Attribute(draco, decoder, mesh, draco.NORMAL, 3, numVertices)
    const uvs = extractFloat32Attribute(draco, decoder, mesh, draco.TEX_COORD, 2, numVertices)

    let subObjectIds: Float32Array | null = null
    let materialIds: Float32Array | null = null
    let subObjectNames: string[] | null = null
    let materialNames: string[] | null = null

    const metadataQuerier = new draco.MetadataQuerier()
    const meshMetadata = decoder.GetMetadata(mesh)

    for (let i = 0; i < numAttributes; i++) {
      const attr = decoder.GetAttribute(mesh, i)
      const attrMetadata = decoder.GetAttributeMetadata(mesh, i)

      if (!attrMetadata || attrMetadata.ptr === 0) {
        continue
      }

      if (metadataQuerier.HasEntry(attrMetadata, 'name')) {
        const attrName = metadataQuerier.GetStringEntry(attrMetadata, 'name')

        if (attrName === 'sub_obj') {
          subObjectIds = extractFloat32AttributeById(draco, decoder, mesh, i, attr.num_components(), numVertices)
          subObjectNames = extractNamesFromMetadata(metadataQuerier, attrMetadata)
        } else if (attrName === 'material') {
          materialIds = extractFloat32AttributeById(draco, decoder, mesh, i, attr.num_components(), numVertices)
          materialNames = extractNamesFromMetadata(metadataQuerier, attrMetadata)
        }
      }
    }

    if (!subObjectNames && meshMetadata && meshMetadata.ptr !== 0) {
      subObjectNames = extractNamesFromMetadata(metadataQuerier, meshMetadata)
    }

    draco.destroy(metadataQuerier)

    const indices = extractIndices(draco, decoder, mesh, numFaces)

    if (!subObjectIds) {
      subObjectIds = new Float32Array(numVertices)
    }

    return {
      numVertices: numVertices,
      numFaces: numFaces,
      positions: positions,
      normals: normals,
      uvs: uvs,
      indices: indices,
      subObjectIds: subObjectIds,
      materialIds: materialIds,
      subObjectNames: subObjectNames,
      materialNames: materialNames,
    }
  } finally {
    draco.destroy(mesh)
    draco.destroy(decoderBuffer)
    draco.destroy(decoder)
  }
}

function extractFloat32Attribute(
  draco: DracoModule,
  decoder: DracoDecoder,
  mesh: DracoMesh,
  attrType: number,
  components: number,
  numVertices: number
): Float32Array | null {
  const attrId = decoder.GetAttributeId(mesh, attrType)
  if (attrId < 0) {
    return null
  }

  return extractFloat32AttributeById(draco, decoder, mesh, attrId, components, numVertices)
}

function extractFloat32AttributeById(
  draco: DracoModule,
  decoder: DracoDecoder,
  mesh: DracoMesh,
  attrId: number,
  components: number,
  numVertices: number
): Float32Array {
  const attr = decoder.GetAttribute(mesh, attrId)
  const dracoArray = new draco.DracoFloat32Array()

  decoder.GetAttributeFloatForAllPoints(mesh, attr, dracoArray)

  const totalValues = numVertices * components
  const result = new Float32Array(totalValues)
  for (let i = 0; i < totalValues; i++) {
    result[i] = dracoArray.GetValue(i)
  }

  draco.destroy(dracoArray)
  return result
}

function extractIndices(draco: DracoModule, decoder: DracoDecoder, mesh: DracoMesh, numFaces: number): Uint32Array {
  const faceArray = new draco.DracoUInt32Array()
  const indices = new Uint32Array(numFaces * 3)

  for (let i = 0; i < numFaces; i++) {
    decoder.GetFaceFromMesh(mesh, i, faceArray)
    indices[i * 3] = faceArray.GetValue(0)
    indices[i * 3 + 1] = faceArray.GetValue(1)
    indices[i * 3 + 2] = faceArray.GetValue(2)
  }

  draco.destroy(faceArray)
  return indices
}

function extractNamesFromMetadata(querier: DracoMetadataQuerier, metadata: DracoMetadata): string[] | null {
  const numEntries = querier.NumEntries(metadata)
  const entries: Array<{ name: string; id: number }> = []

  for (let i = 0; i < numEntries; i++) {
    const key = querier.GetEntryName(metadata, i)
    if (key === 'name') {
      continue
    }

    try {
      const id = querier.GetIntEntry(metadata, key)
      entries.push({ name: key, id: id })
    } catch (_error) {
      // Skip non-integer metadata entries.
    }
  }

  return buildIndexedNameArray(entries)
}

function computeViewerSpaceBounds(positions: Float32Array): {
  center: { x: number; y: number; z: number }
  size: { x: number; y: number; z: number }
} {
  if (positions.length === 0) {
    return {
      center: { x: 0, y: 0, z: 0 },
      size: { x: 0, y: 0, z: 0 },
    }
  }

  const min = { x: Infinity, y: Infinity, z: Infinity }
  const max = { x: -Infinity, y: -Infinity, z: -Infinity }

  for (let i = 0; i < positions.length; i += 3) {
    const x = -positions[i]
    const y = positions[i + 1]
    const z = -positions[i + 2]

    if (x < min.x) { min.x = x }
    if (y < min.y) { min.y = y }
    if (z < min.z) { min.z = z }
    if (x > max.x) { max.x = x }
    if (y > max.y) { max.y = y }
    if (z > max.z) { max.z = z }
  }

  return {
    center: {
      x: (min.x + max.x) / 2,
      y: (min.y + max.y) / 2,
      z: (min.z + max.z) / 2,
    },
    size: {
      x: max.x - min.x,
      y: max.y - min.y,
      z: max.z - min.z,
    },
  }
}

export async function decodeRecoveredBoundsFromBuffer(
  buffer: ArrayBuffer,
  options: DracoDecodeOptions
): Promise<DecodedRecoveredSubObjectBounds[]> {
  const draco = await getDecoder(options)
  const flatMesh = decodeMesh(draco, buffer)
  const subObjects = splitSubObjects(flatMesh)

  return subObjects.map(function (subObject) {
    const bounds = computeViewerSpaceBounds(subObject.positions)
    return {
      name: subObject.name,
      materialName: subObject.materialName,
      center: bounds.center,
      size: bounds.size,
    }
  })
}
